export function getFilesFromInput(input: HTMLInputElement): File[] {
  return Array.from(input.files ?? []);
}
export async function fileToDataUrl(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const b64 = Buffer.from(buf).toString('base64');
  return `data:${file.type};base64,${b64}`;
}
export async function uploadListingImage(file: File, path: string): Promise<{ url: string }> {
  // plug into your storage; return { url }
  return { url: path };
}
type Listingish = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const arrStr = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

/**
 * Convert storage key to public URL if needed.
 * Pure string builder - no runtime dependencies, no Supabase client.
 * 
 * Rules:
 * - If input is empty → return ""
 * - If starts with "http", "blob:", or "data:" → return as-is
 * - Otherwise → treat as storage key and construct public URL
 * 
 * @param value - Storage key or full URL
 * @returns Public URL string
 */
export function resolveImageUrl(value: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  
  // If already a full URL (http/https), blob URL, or data URL, return as-is
  if (value.startsWith('http') || value.startsWith('blob:') || value.startsWith('data:')) {
    return value;
  }
  
  // Treat as storage key and construct public URL
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
  if (!supabaseUrl) {
    // Fallback: return original value if env var is missing
    return value;
  }
  
  // Remove trailing slash from supabaseUrl if present
  const baseUrl = supabaseUrl.replace(/\/$/, '');
  
  // Construct public URL: {SUPABASE_URL}/storage/v1/object/public/listing-images/{key}
  return `${baseUrl}/storage/v1/object/public/listing-images/${value}`;
}

/**
 * Alias for resolveImageUrl for backward compatibility.
 */
export const safeResolveImageUrl = resolveImageUrl;

export function coverUrlFromListing(l: Listingish): string {
  const raw = (
    str(l['cover_image_url']) ||
    str(l['cover_url']) ||
    str(l['hero_url']) ||
    str(l['image_url']) ||
    (arrStr(l['images'])[0] ?? '') ||
    (typeof l['image_urls'] === 'string'
      ? l['image_urls'].split(',').map((s) => s.trim()).filter(Boolean)[0] ?? ''
      : '')
  );
  
  if (!raw) return '';
  
  // Convert storage key to public URL if needed
  return resolveImageUrl(raw);
}

export function galleryFromListing(l: Listingish): string[] {
  // Try multiple possible field names for images
  const images = arrStr(l['images']) || arrStr(l['gallery']) || [];
  const fromCsv =
    typeof l['image_urls'] === 'string'
      ? l['image_urls'].split(',').map((s) => s.trim()).filter(Boolean)
      : [];
  
  // Combine all possible image sources
  const allImages = [...images, ...fromCsv].filter(Boolean);
  
  // Remove duplicates
  const uniqueImages = [...new Set(allImages)];
  
  // Convert storage keys to public URLs
  return uniqueImages.map(resolveImageUrl);
}

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || '';

export const SUPABASE_HOSTNAME = (() => {
  if (!SUPABASE_URL) return '';
  try {
    return new URL(SUPABASE_URL).hostname;
  } catch (error) {
    console.warn('images.ts: failed to parse NEXT_PUBLIC_SUPABASE_URL', error);
    return '';
  }
})();

export const SUPABASE_PUBLIC_BASE = SUPABASE_HOSTNAME ? `https://${SUPABASE_HOSTNAME}` : '';
export const SUPABASE_STORAGE_PUBLIC_BASE = SUPABASE_PUBLIC_BASE
  ? `${SUPABASE_PUBLIC_BASE}/storage/v1/object/public/`
  : '';

export const supabaseImageLoader = ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
  try {
    const url = new URL(src);
    url.searchParams.set('width', String(width));
    if (quality) {
      url.searchParams.set('quality', String(quality));
    }
    return url.toString();
  } catch {
    // If parsing fails (e.g. relative path), return original src
    return src;
  }
};

export const isSupabaseStorageUrl = (url?: string | null) => {
  if (!url || !SUPABASE_STORAGE_PUBLIC_BASE) return false;
  return url.startsWith(SUPABASE_STORAGE_PUBLIC_BASE);
};
